<template>
  <Sidebar
    v-model:visible="createNewSidebarVisible"
    class="p-sidebar-lg"
    position="right"
    :showCloseIcon="false"
  >
    <div>
      <div class="flex flex-column mb-3">
        <h3 class="mb-1">Create new offer</h3>
      </div>
      <Form
        :fields="filteredOfferFormFields"
        :context="newEntity"
        :actionLabel="'Add to '"
        @onChange="onOfferFormChange"
        @onAction="addOffer"
        :actionIcon="'pi pi-plus-circle'"
      />
    </div>
  </Sidebar>

  <Sidebar
    v-model:visible="editOfferSidebarVisible"
    class="p-sidebar-lg"
    position="right"
    :showCloseIcon="false"
  >
    <div>
      <div class="flex flex-column mb-3">
        <h3 class="mb-1">Update offer</h3>
      </div>
      <Form
        :fields="filteredOfferFormFields"
        :context="editEntity"
        :actionLabel="'Update offer'"
        @onChange="onOfferFormChange"
        @onAction="updateOffer"
        :actionIcon="'pi pi-plus-circle'"
      />
    </div>
  </Sidebar>

  <div>
    <div class="px-6 w-full flex flex-wrap">
      <div>
        <p class="font-bold text-900 text-lg m-0">
          {{ title }}
        </p>
        <p class="m-0 text-sm text-700 mb-3">
          {{ description }}
        </p>
      </div>
      <div class="ml-auto align-items-center w-full md:w-fit md:flex">
        <Button
          class="h-min bg-green-400 border-green-400"
          :label="'Save'"
          icon="pi pi-cloud-upload"
          @click="onSave"
        />
        <Button
          class="ml-2 h-min"
          :label="'Add offer'"
          icon="pi pi-plus-circle"
          @click="showAddNewSideBar"
        />
      </div>
    </div>

    <div class="w-full flex mt-6">
      <div class="card mx-auto">
        <OfferList
          :offers="offers"
          @onEditOffer="showEditSideBar"
          @onRemoveOffer="removeOffer"
          @onMoveOffer="updateOrderIndex"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref, computed } from "vue";
import OfferList from "./OfferList.vue";
import {
  discountOnMembershipOfferFormFields,
  freeMembershipOfferFormFields,
  getPerkOfferFormFields,
} from "../formValues";
import { useStore } from "vuex";
import { uploadFile } from "@/services/fileUploadService";
import { useToast } from "primevue/usetoast";

export default {
  components: {
    OfferList,
  },
  props: ["catchOffers", "title", "description"],
  emits: ["onUpdate"],
  setup(props, { emit }) {
    const store = useStore();
    const toast = useToast();
    const actionGetPerks = "perks/getPerkListItems";
    const getterGetPerks = "perks/perkListItems";

    const createNewSidebarVisible = ref(false);
    const editOfferSidebarVisible = ref(false);

    // Timezone detection
    const detectedTimeZone = computed(() => {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    });

    const perks = ref([]);
    const offerFormFields = ref([]);
    const offers = ref(props.catchOffers ?? []);
    
    // Computed property to filter fields based on conditions
    const filteredOfferFormFields = computed(() => {
      return offerFormFields.value.filter(field => {
        if (!field.condition) return true;
        // Check against the current form context (newEntity or editEntity)
        const context = createNewSidebarVisible.value ? newEntity.value : editEntity.value;
        return context && context[field.condition] === true;
      });
    });

    const emptyTranslations = {
      translations: [
        { languageCode: "en", text: "" },
        { languageCode: "da", text: "" },
        { languageCode: "fi", text: "" },
        { languageCode: "no", text: "" },
      ],
    };

    const fixedTermsTranslations = {
      translations: [
        { languageCode: "en", text: "(Valid from {{next_invoice_date}}" },
        { languageCode: "da", text: "(Gældende fra {{next_invoice_date}}" },
        { languageCode: "fi", text: "(Voimassa {{next_invoice_date}}" },
        { languageCode: "no", text: "(Gjelder fra {{next_invoice_date}}" },
      ],
    };
    const newEntity = ref({
      titleLocalized: { ...emptyTranslations },
      termsLocalized: { ...emptyTranslations },
      amount: 1,
      iconUrl: undefined,
      offerType: "Perk",
      perkId: undefined,
      orderIndex: 0,
      newEntity: true,
      // PerkRedemptionTimeControl fields
      isRedemptionTimeLimited: false,
      lockedFromTime: null,
      unlockedAtTime: null,
      timeZoneId: detectedTimeZone.value,
    });

    const editEntity = ref({
      titleLocalized: { ...emptyTranslations },
      termsLocalized: { ...emptyTranslations },
      amount: 1,
      iconUrl: undefined,
      offerType: "Perk",
      perkId: undefined,
      orderIndex: 0,
      newEntity: false,
      // PerkRedemptionTimeControl fields
      isRedemptionTimeLimited: false,
      lockedFromTime: null,
      unlockedAtTime: null,
      timeZoneId: detectedTimeZone.value,
    });

    // PerkRedemptionTimeControl helper functions
    function parseTimeToDate(timeString) {
      if (!timeString) return null;
      const [hours, minutes] = timeString.split(':').map(num => parseInt(num, 10));
      
      // Create a date object in local time
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      
      return date;
    }

    function initializeRedemptionTimeControl(offer) {
      if (offer.perkRedemptionTimeControl) {
        // Initialize from existing PerkRedemptionTimeControl
        offer.isRedemptionTimeLimited = offer.perkRedemptionTimeControl.isRedemptionTimeLimited || false;
        // Convert time strings to Date objects for the Calendar components
        offer.lockedFromTime = parseTimeToDate(offer.perkRedemptionTimeControl.lockedFromTime);
        offer.unlockedAtTime = parseTimeToDate(offer.perkRedemptionTimeControl.unlockedAtTime);
        offer.timeZoneId = offer.perkRedemptionTimeControl.timeZoneId || detectedTimeZone.value;
      } else {
        // Initialize with defaults
        offer.isRedemptionTimeLimited = false;
        offer.lockedFromTime = null;
        offer.unlockedAtTime = null;
        offer.timeZoneId = detectedTimeZone.value;
      }
    }

    function convertTimeToString(timeValue) {
      if (!timeValue) return null;
      
      if (timeValue instanceof Date) {
        const hours = timeValue.getHours().toString().padStart(2, '0');
        const minutes = timeValue.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}:00`;
      } else if (typeof timeValue === 'string') {
        // Handle ISO datetime strings or time strings
        if (timeValue.includes('T') || timeValue.includes('Z')) {
          // It's an ISO datetime string, extract time part
          const date = new Date(timeValue);
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          return `${hours}:${minutes}:00`;
        }
        // If it's already in HH:mm format, add seconds
        if (timeValue.match(/^\d{2}:\d{2}$/)) {
          return `${timeValue}:00`;
        }
        // If it's already in HH:mm:ss format, leave it as is
        return timeValue;
      }
      
      return null;
    }

    function showAddNewSideBar() {
      createNewSidebarVisible.value = true;
    }

    function showEditSideBar(offer) {
      onFormChange(offer);
      editEntity.value = { ...offer, newEntity: false };
      
      // Initialize PerkRedemptionTimeControl fields for editing
      initializeRedemptionTimeControl(editEntity.value);
      
      editOfferSidebarVisible.value = true;
    }

    function updateOrderIndex(indexes) {
      if (
        offers.value.length == 1 ||
        (indexes.oldIndex == 0 && indexes.newIndex <= indexes.oldIndex) ||
        indexes.newIndex == offers.value.length
      )
        return;

      var offer = offers.value.at(indexes.oldIndex);
      offer.orderIndex = indexes.newIndex;
      offers.value.splice(indexes.oldIndex, 1);
      offers.value.splice(indexes.newIndex, 0, offer);
    }

    function removeOffer(index) {
      offers.value.splice(index, 1);
    }

    // Form change handler for conditional field rendering
    function onOfferFormChange(context) {
      // Update the appropriate entity context
      if (createNewSidebarVisible.value) {
        Object.assign(newEntity.value, context);
      } else if (editOfferSidebarVisible.value) {
        Object.assign(editEntity.value, context);
      }
      
      // Also call the original form change logic for offer type changes
      onFormChange(context);
    }

    function onFormChange(value) {
      if (value.offerType == "FreeMembershipMonth") {
        offerFormFields.value = freeMembershipOfferFormFields;
        editEntity.value.perkId = undefined;
        newEntity.value.perkId = undefined;
        editEntity.value.termsLocalized = fixedTermsTranslations;
        newEntity.value.termsLocalized = fixedTermsTranslations;
      }

      if (!value.newEntity && editEntity.value.offerType == value.offerType)
        return;

      if (value.offerType == "DiscountOnMembership") {
        offerFormFields.value = discountOnMembershipOfferFormFields;
        editEntity.value.perkId = undefined;
        newEntity.value.perkId = undefined;
        editEntity.value.termsLocalized = { ...emptyTranslations };
        newEntity.value.termsLocalized = { ...emptyTranslations };
      }
      if (value.offerType == "Perk") {
        offerFormFields.value = getPerkOfferFormFields(perks.value);
        editEntity.value.termsLocalized = { ...emptyTranslations };
        newEntity.value.termsLocalized = { ...emptyTranslations };
      }

      editEntity.value.offerType = value.offerType;
      newEntity.value.offerType = value.offerType;
    }

    async function addOffer(offer) {
      var valid = validateFormInputs(offer);
      if (!valid) return;

      var url = await handleIconUpload(offer.iconUrl);
      offer.iconUrl = url;
      offer.orderIndex = offers.value?.length;

      // Handle PerkRedemptionTimeControl for Perk offers
      if (offer.offerType === "Perk") {
        const perkRedemptionTimeControl = {
          isRedemptionTimeLimited: offer.isRedemptionTimeLimited || false,
          lockedFromTime: null,
          unlockedAtTime: null,
          timeZoneId: offer.timeZoneId || detectedTimeZone.value
        };

        // Convert time values if redemption time is limited
        if (offer.isRedemptionTimeLimited) {
          perkRedemptionTimeControl.lockedFromTime = convertTimeToString(offer.lockedFromTime);
          perkRedemptionTimeControl.unlockedAtTime = convertTimeToString(offer.unlockedAtTime);
        }

        offer.perkRedemptionTimeControl = perkRedemptionTimeControl;

        // Remove individual time control fields from the offer object
        delete offer.isRedemptionTimeLimited;
        delete offer.lockedFromTime;
        delete offer.unlockedAtTime;
        delete offer.timeZoneId;
      }

      console.log(offer);
      offers.value.push(offer);
      createNewSidebarVisible.value = false;
    }

    async function updateOffer(updatedOffer) {
      var valid = validateFormInputs(updatedOffer, true);
      if (!valid) return;

      var offer = offers.value.at(updatedOffer.orderIndex);

      var url = offer.iconUrl;
      if (updatedOffer.iconUrl != url) {
        url = await handleIconUpload(updatedOffer.iconUrl);
      }
      updatedOffer.iconUrl = url;

      // Handle PerkRedemptionTimeControl for Perk offers
      if (updatedOffer.offerType === "Perk") {
        const perkRedemptionTimeControl = {
          isRedemptionTimeLimited: updatedOffer.isRedemptionTimeLimited || false,
          lockedFromTime: null,
          unlockedAtTime: null,
          timeZoneId: updatedOffer.timeZoneId || detectedTimeZone.value
        };

        // Convert time values if redemption time is limited
        if (updatedOffer.isRedemptionTimeLimited) {
          perkRedemptionTimeControl.lockedFromTime = convertTimeToString(updatedOffer.lockedFromTime);
          perkRedemptionTimeControl.unlockedAtTime = convertTimeToString(updatedOffer.unlockedAtTime);
        }

        updatedOffer.perkRedemptionTimeControl = perkRedemptionTimeControl;

        // Remove individual time control fields from the offer object
        delete updatedOffer.isRedemptionTimeLimited;
        delete updatedOffer.lockedFromTime;
        delete updatedOffer.unlockedAtTime;
        delete updatedOffer.timeZoneId;
      }

      offers.value.splice(updatedOffer.orderIndex, 1, updatedOffer);
      editOfferSidebarVisible.value = false;
    }

    async function handleIconUpload(image) {
      var logoFile = await uploadFile(image, "card-icons", "all", store);
      return logoFile.url;
    }

    async function getPerks() {
      await store.dispatch(actionGetPerks);
      perks.value = store.getters[getterGetPerks];
      offerFormFields.value = getPerkOfferFormFields(perks.value);
    }
    function validateFormInputs(offer, isUpdate = false) {
      if (offer.amount <= 0 || offer.iconUrl == null || offer.iconUrl == "") {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Please fill out all values in form",
          life: 3000,
        });
        return false;
      }

      if (
        offer.offerType == "Perk" &&
        (offer.perkId == null || offer.perkId == "")
      ) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Please fill out all values in form",
          life: 3000,
        });
        return false;
      }

      var freeMembershipMonthOffer = offers.value?.find(
        (x) => x.offerType == "FreeMembershipMonth"
      );
      var discountOnMembershipOffer = offers.value?.find(
        (x) => x.offerType == "DiscountOnMembership"
      );

      if (
        !isUpdate &&
        ((offer.offerType != "Perk" && freeMembershipMonthOffer != null) ||
          (offer.offerType != "Perk" && discountOnMembershipOffer != null))
      ) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Not possible to add 2 membership offers",
          life: 3000,
        });
        return false;
      }

      return true;
    }

    function onSave() {
      emit("onUpdate", offers.value);
    }

    onMounted(() => {
      getPerks();
      
      // Initialize PerkRedemptionTimeControl for existing offers
      if (offers.value && offers.value.length > 0) {
        offers.value.forEach(offer => {
          if (offer.offerType === "Perk") {
            initializeRedemptionTimeControl(offer);
          }
        });
      }
    });

    return {
      newEntity,
      editEntity,
      offerFormFields,
      filteredOfferFormFields,
      createNewSidebarVisible,
      editOfferSidebarVisible,
      perks,
      offers,

      showAddNewSideBar,
      showEditSideBar,
      onFormChange,
      onOfferFormChange,
      updateOrderIndex,
      removeOffer,
      addOffer,
      updateOffer,
      onSave,
    };
  },
};
</script>

<style scoped>
.card {
  min-height: 28rem;
  border-radius: 4px;
  width: 440px;
  background-image: linear-gradient(
    to right top,
    #081654,
    #081654,
    #122886,
    #122886,
    #122886
  );
}
.htmleditor {
  background: #081654;
}
</style>
